// Encapsulate functionality to be exported into a global
const partnersAppIntegrationModule = (() => {
	// Shared country name mapping - single source of truth
	const COUNTRY_NAME_MAP = {
		'AT': 'Austria', 'BE': 'Belgium', 'BG': 'Bulgaria', 'HR': 'Croatia',
		'CY': 'Cyprus', 'CZ': 'Czech Republic', 'DK': 'Denmark', 'EE': 'Estonia',
		'FI': 'Finland', 'GR': 'Greece', 'HU': 'Hungary', 'IE': 'Ireland',
		'IT': 'Italy', 'LV': 'Latvia', 'LT': 'Lithuania', 'LU': 'Luxembourg',
		'MT': 'Malta', 'NL': 'Netherlands', 'PL': 'Poland', 'PT': 'Portugal',
		'RO': 'Romania', 'SK': 'Slovakia', 'SI': 'Slovenia', 'ES': 'Spain',
		'SE': 'Sweden', 'NO': 'Norway', 'LI': 'Liechtenstein', 'IS': 'Iceland',
		'CH': 'Switzerland', 'AL': 'Albania', 'AD': 'Andorra', 'BY': 'Belarus',
		'BA': 'Bosnia and Herzegovina', 'FO': 'Faroe Islands', 'GI': 'Gibraltar',
		'GG': 'Guernsey', 'VA': 'Holy See (Vatican City State)', 'IM': 'Isle of Man', 'JE': 'Jersey',
		'XK': 'Kosovo', 'MK': 'Macedonia', 'MD': 'Moldova', 'MC': 'Monaco',
		'ME': 'Montenegro', 'RU': 'Russian Federation', 'SM': 'San Marino', 'RS': 'Serbia',
		'SJ': 'Svalbard and Jan Mayen', 'UA': 'Ukraine',
		// Common non-EU countries for completeness
		'US': 'United States', 'CA': 'Canada', 'AU': 'Australia', 'NZ': 'New Zealand',
		'GB': 'United Kingdom', 'FR': 'France', 'DE': 'Germany'
	};

	// Set up our constants that our module extends to the partner landing pages.
	const partnerLpAppSettings = {
		// Set up our constants that our module extends to the partner landing pages.
		registerServer: "https://ss15.shipstation.com", // Default US Server.
		registerServerEU: "https://ss8.shipstation.com", // EU Server (Ireland/Dublin)
		oauthServer: "https://ss2.shipstation.com", // OAuth server for all partner integrations
		registerEndpoint: "https://ss2.shipstation.com/api/account/proxyRegisterPartnerAndGetToken",
		registerEndpointProxyRegistration: "https://ss2.shipstation.com/api/account/ProxyRegistration",
		// GDPR/Privacy consent required for all EU/EEA countries + UK
		optinConsentCountries: [
			// EU Member States (27)
			"AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", 
			"DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", 
			"PL", "PT", "RO", "SK", "SI", "ES", "SE",
			// EEA Members (3)
			"IS", "LI", "NO",
			// UK (Brexit but similar privacy requirements)
			"GB"
		],
		step2Redirect: "https://ship.shipstation.com/login", // Deprecated: Use executePartnerRedirect instead
	};

	/**
	 * Build query parameters from an object, filtering out empty values
	 * @param {Object} params - Parameters to convert to query string
	 * @returns {string} Query string with leading "?" or empty string
	 */
	function queryParamFromObject(params) {
		if (!params) return "";

		let searchParams = new URLSearchParams();

		for (const key in params) {
			if (Object.hasOwnProperty.call(params, key)) {
				const paramName = key;
				const paramValue = params[key];

				if (paramValue) {
					searchParams.append(paramName, paramValue);
				}
			}
		}

		return "?" + searchParams.toString();
	}

	/**
	 * Get the appropriate step2 redirect URL based on country
	 * @param {string} country - Country code (US, GB, FR, etc.)
	 * @returns {string} Step2 redirect path or null
	 */
	function getStep2RedirectPath(country) {
		const countriesWithStep2 = []; // Add countries that need step2 if any
		
		if (countriesWithStep2.includes(country)) {
			if (country === "FR") {
				return "/step2-fr/";
			}
			if (country === "GB") {
				return "/uk/step2/";
			}
			return "/step2/";
		}
		
		// Default to dashboard login
		return null;
	}

	/**
	 * Build the final redirect URL with auto-login support
	 * Fixed to match register.js logic flow exactly
	 * @param {Object} config - Configuration object
	 * @returns {string} Final redirect URL with appropriate parameters
	 */
	function buildPartnerRedirectUrl(config) {
		const {
			baseRedirectUrl,
			partitionId,
			authToken,
			autoLoginNonce,
			username,
			country = 'US',
			onboarding = true
		} = config;

		// Start with base server URL (e.g., "https://ss15.shipstation.com")
		let step2Redirect = baseRedirectUrl;

		// STEP 1: Apply partition-based server routing (BEFORE Auth0 check - matches register.js)
		if (partitionId && authToken) {
			step2Redirect = step2Redirect.replace(/^https:\/\/ss\d+\./, `https://ss${partitionId}.`);
		}
		
		// Add /login path to the server URL
		step2Redirect = step2Redirect + '/login';
		
		// STEP 2: Add query parameters for legacy flow (BEFORE Auth0 check - matches register.js)
		const queryParam = queryParamFromObject({
			firsttime: true,
			a: partitionId && authToken ? authToken : null
		});
		
		step2Redirect = step2Redirect + queryParam;

		// STEP 3: Check for step2 redirects (matches register.js logic)
		const countriesWithStep2 = []; // Currently empty in register.js
		if (step2Redirect.includes('step2') || countriesWithStep2.includes(country)) {
			step2Redirect = "/step2/";
			if (country === "FR") {
				step2Redirect = "/step2-fr/";
			}
			if (country === "GB") {
				step2Redirect = "/uk/step2/";
			}
		}

		// STEP 4: Determine final redirect URL based on presence of autoLoginNonce
		let finalRedirectUrl;

		// Debug override: Force legacy flow if URL parameter present
		const forceLegacy = new URLSearchParams(window.location.search).get('forcelegacy') === 'true';
		const useAuth0Flow = !forceLegacy && autoLoginNonce && autoLoginNonce !== false && autoLoginNonce !== '';

		if (useAuth0Flow) {
			// Auth0 flow: Use ship prefix with partition ID (fallback to ss14 if null)
			const effectivePartitionId = partitionId || '14';
			const baseUrl = new URL(`https://ship${effectivePartitionId}.shipstation.com`);
			
			// Clear all existing query parameters to avoid legacy parameter conflicts
			baseUrl.search = '';
			
			// Remove /login path for Auth0 flow
			if (baseUrl.pathname === '/login') {
				baseUrl.pathname = '';
			}

			// Add Auth0-specific parameters (nonce acts as the "first time" indicator)
			baseUrl.searchParams.set('onboarding', onboarding);
			baseUrl.searchParams.set('username', username);
			baseUrl.searchParams.set('nonce', autoLoginNonce);

			finalRedirectUrl = baseUrl.toString();

		} else {
			// Legacy flow: use the URL with all existing parameters
			finalRedirectUrl = step2Redirect;
		}

		return finalRedirectUrl;
	}

	/**
	 * Execute partner redirect with auto-login support
	 * Replaces hardcoded 'window.location = _registerServer' patterns
	 * 
	 * @param {Object} registrationResponse - Response from registration API
	 * @param {Object} options - Additional options
	 */
	function executePartnerRedirect(registrationResponse, options = {}) {
		const {
			partitionId,
			authToken,
			autoLoginNonce,
			username
		} = registrationResponse;

		const {
			// Always start with country-based server, let dynamic routing handle partition override
			baseRedirectUrl = getPartnerBaseRedirectUrl(options.country || 'US'),
			country = 'US',
			delay = 1000,
			onboarding = true
		} = options;

		// Build the final redirect URL with auto-login support
		const finalRedirectUrl = buildPartnerRedirectUrl({
			baseRedirectUrl,
			partitionId,
			authToken,
			autoLoginNonce,
			username,
			country,
			onboarding
		});

		// Development Mode: Show URL instead of redirecting for local testing
		const showUrlInstead = new URLSearchParams(window.location.search).get('debug') === 'show' || 
		                       new URLSearchParams(window.location.search).get('debugshow') === 'true';
		
		if (showUrlInstead) {
			alert('🔧 DEBUG MODE\n\nGenerated redirect URL:\n' + finalRedirectUrl + '\n\nCheck console for details.');
			return;
		}

		// Execute the redirect with delay (matching register.js pattern)
		setTimeout(function() {
			window.location.replace(finalRedirectUrl);
		}, delay);
	}

	/**
	 * Check if a country should use the EU server
	 * @param {string} country - Country code
	 * @returns {boolean} True if country should use EU server
	 */
	function isEuServerCountry(country) {
		// Comprehensive list of countries that should use EU server (ss8)
		const euServerCountries = [
			'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 
			'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 
			'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'NO', 'LI', 
			'IS', 'CH', 'AL', 'AD', 'BY', 'BA', 'FO', 'GI', 'GG', 'VA', 
			'IM', 'JE', 'XK', 'MK', 'MD', 'MC', 'ME', 'RU', 'SM', 'RS', 
			'SJ', 'UA'
		];
		
		return euServerCountries.includes(country?.toUpperCase());
	}

	/**
	 * Get the base server URL based on country (fallback for legacy compatibility)
	 * @param {string} country - Country code
	 * @returns {string} Base server URL
	 * @deprecated Use dynamic partition-based URLs when partitionId is available
	 */
	function getPartnerBaseRedirectUrl(country) {
		// Return server URL directly - no unnecessary transformations
		if (isEuServerCountry(country)) {
			return partnerLpAppSettings.registerServerEU; // "https://ss8.shipstation.com"
		}
		return partnerLpAppSettings.registerServer; // "https://ss15.shipstation.com"
	}

	/**
	 * Get dynamic server URL based on partitionId (preferred method)
	 * @param {string|number} partitionId - Partition ID from registration response
	 * @returns {string} Dynamic server URL
	 */
	/**
	 * Parse our parameters from the URL and return the values.
	 *
	 * @param {string} name The name of the parameter to grab from the URL.
	 * @returns {object} Returns the parameters from the URL.
	 */
	function getParameterByName(name) {
		name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
		var regex = new RegExp("[\\?&]" + name + "=([^&#]*)"),

		results = regex.exec(location.search);

		return results == null
		  ? ""
		  : decodeURIComponent(results[1].replace(/\+/g, " "));
	}

	/**
	 * Get the ShipStation URL based on the country.
	 *
	 * @param {string} oauthRoute String for the OAuth route.
	 * @param {string} registerServerUK Url for the UK server.
	 * @param {string} registerServer  Url for the default server.
	 * @param {string} country Country code.
	 * @param {object} [additionalParams] Extra query params to forward onto the
	 *   redirect (e.g. `state`), keyed by param name. Only appended when `code` is
	 *   also present, and only for the caller that passes them — other partners are
	 *   unaffected.
	 * @returns string Returns the ShipStation URL.
	 */
	function getShipStationUrl( oauthRoute, registerServerUK, registerServer, country, additionalParams ) {
		let server;

		// If the country is GB, FR, or DE, use the UK server for registration.
		if (country == "GB" || country == "FR" || country == "DE") {
			server = registerServerUK;
		} else {
			server = registerServer;
		}

		let code = getParameterByName("code");

		if (code != "") {
			server += oauthRoute + "?code=" + code;

			if (additionalParams) {
				for (const [key, value] of Object.entries(additionalParams)) {
					if (value) {
						server += "&" + key + "=" + encodeURIComponent(value);
					}
				}
			}
		}

		return server;
	}

	/**
	 * Grab our home country from the cookie or the dropdown selector.
	 *
	 * @returns {string} Returns the home country.
	 */
	function getHomeCountry() {
		// Check URL parameter first (for testing/debugging)
		let urlCountry = getParameterByName("country");
		if (urlCountry && urlCountry !== "") {
			return urlCountry.toUpperCase();
		}
		
		// Check form's countryCode field (hidden input)
		let formCountryCode = $('input[name="countryCode"]').val();
		if ($('input[name="countryCode"]').length && $('input[name="countryCode"]').val() != "") {
			return $('input[name="countryCode"]').val();
		}
		
		// Check visible country dropdown
		let dropdownCountry = $("#country").val();
		if ($("#country").length && $("#country").val() != "") {
			return $("#country").val();
		}
		
		// Check cookie
		let cookieCountry = $.cookie("home_country");
		if ($.cookie("home_country") && $.cookie("home_country") != "") {
			return $.cookie("home_country");
		}
		
		return "US";
	}

	/**
	 * Handles the "How did you hear about us?" dropdown and textfield.
	 */
	function ss_handleHowDidYouHearAboutUs() {
		// Attach event handler using .on() for modern jQuery versions
		$("select#howdidyouhearaboutus").on('change', function () {
			if ($("select#howdidyouhearaboutus option:selected").text() == "Other (Please specify below)") {
				$("#howdidyouhearaboutus_other_container").fadeIn();
			} else {
				$("#howdidyouhearaboutus_other_container").hide();
			}
		});
	
		// Handle changes to the textfield using .on()
		$("input[name=howdidyouhearaboutus_textfield]").on('change', function () {
			$("#howdidyouhearaboutus_other").val($(this).val());
		});
	}
	
	/**
	 * Changes our shipment volume dropdown options based on the country selected.
	 *
	 * @param {string} country Country value from dropdown selector.
	 * @param {string} shipmentVolumeSelectorString The HTML selector string for the shipment volume dropdown.
	 */
	function updateEuShipmentVolumeSelectOptions(country, shipmentVolumeSelectorString) {
		const shipmentVolumeSelector = $(shipmentVolumeSelectorString) ?? $("#shipment_volume");
		const originalShipmentOptions = $(shipmentVolumeSelectorString).html() ?? $("#shipment_volume").html();
		const euCountries = ['GB', 'FR', 'DE', 'UK'];
	  
		const euShipmentVolumes = [
		  { value: "1", text: "I only need to ship one package" },
		  { value: "50", text: "50 shipments or less, I'm just getting started" },
		  { value: "500", text: "51-500 shipments" },
		  { value: "3000", text: "501-3,000 shipments" },
		  { value: "10000", text: "3,001-10,000 shipments" },
		  { value: "10001", text: "10,001+ shipments" },
		];
	  
		let shipmentVolumeHtmlString = `<option value="default" selected disabled>Select your current number of parcel shipments per month</option>`;
	  
		for (const euShipmentVolume of euShipmentVolumes) {
		  shipmentVolumeHtmlString += `<option value="${euShipmentVolume.value}">${euShipmentVolume.text}</option>`;
		}
		// If EU, change shipment volume options
		if (euCountries.includes(country)) {
		  shipmentVolumeSelector.html(shipmentVolumeHtmlString);
		} else {
		  shipmentVolumeSelector.html(originalShipmentOptions);
		}
	}

	// Shipment volume values that count as "50 shipments or less per month"
	// ('1' = single package, '50' = 50 or less). Mirrors js/register.js.
	const TARGET_APP_ELIGIBLE_VOLUMES = ['1', '50'];

	/**
	 * Target app (ShipStation Lite) — ClickUp 86e27tym2. Was an Optimizely A/B test;
	 * it concluded as a WIN for the Lite variation, so it now ships to everyone
	 * eligible. Sets the shared #targetApp hidden field (rendered by forms/signup.php
	 * for every partner LP, same as the default [signup] form) to "lite" for a
	 * low-volume shipper, "default" otherwise. Each single-partner script's
	 * submitHandler must call this before serializing the form — partner scripts bind
	 * their own jQuery Validate submitHandler, so register.js's own updater never runs
	 * on these pages.
	 */
	function updateTargetAppAtSubmission() {
		const shipmentVolume = $("#shipment_volume").length ? $("#shipment_volume").val() : "";
		const isLite = TARGET_APP_ELIGIBLE_VOLUMES.includes(shipmentVolume) && typeof window.ssIsUsGeo === 'function' && window.ssIsUsGeo();

		$("#targetApp").val(isLite ? "lite" : "default");
	}

	/**
	 * Register the country handler for the partner landing pages.
	 * This function will handle the country dropdown and update the terms of service and privacy policy links based on the country selected.
	 */
	function registerCountryHandler(countryCode) {
		// Determine the country code to use - prioritize parameter, fallback to dropdown
		let activeCountryCode = countryCode || ($('select#country').length ? $('select#country option:selected').val() : '');

		if (!activeCountryCode) {
			return;
		}

		// Set cookie for all primary supported countries
		if (['AU', 'CA', 'US', 'GB'].includes(activeCountryCode)) {
			let thirtyDays = 1000 * 60 * 60 * 24 * 60;
			let expires = new Date((new Date()).valueOf() + thirtyDays);

			$.cookie("home_country", activeCountryCode, {
				path: '/', domain: '.shipstation.com', expires: expires
			});
		}

		// Only remove 'required' consent for non-GDPR countries (GB excluded to avoid contradiction)
		if (['AU', 'CA', 'US'].includes(activeCountryCode)) {
			if ($('input[name="optin_consent"]').hasClass('required')) {
				$('input[name="optin_consent"]').removeClass('required').prop('checked', false);
			}
		}

		// Reset consent fields
		$('.optin-consent-field').hide();
		$('input[name=tos]').attr('checked', false);

		// Check if country requires GDPR consent (using our comprehensive list)
		const requiresGdprConsent = partnerLpAppSettings.optinConsentCountries.includes(activeCountryCode);
		
		if (requiresGdprConsent) {
			$('.optin-consent-field').show();
			// GB specifically requires the consent to be mandatory
			if (activeCountryCode === 'GB') {
				$('input[name="optin_consent"]').addClass('required');
				$('.vat_field').show();
			}
		}

		// Set country display name using our mapping (much more concise!)
		const countryName = COUNTRY_NAME_MAP[activeCountryCode] || 'Other';
		$('#country').val(countryName);

		// Update the shipment volume dropdown options based on the country selected.
		updateEuShipmentVolumeSelectOptions(activeCountryCode, '#shipment_volume');

	}



	/**
	 * 
	 * @param {string} key Our global object key
	 * @param {Function} callback Our callback function that we want to use.
	 */
    function waitForGlobalObject(key, callback) {
        if (!jQuery.isEmptyObject(window[key])) {
            callback();
        } else {
            setTimeout(() => {
                waitForGlobalObject(key, callback);
            }, 100);
        }
    }

	/**
	 * Check to seee if we have an experiment running on the page. If so we will update the input field with the experiment data.
	 */
    function checkIfExperimentsExist() {
		$('input#experiments').length = 1;

        if($('input#experiments').length) {
			// Function waits for a global variable object
            waitForGlobalObject('gaData', function() {
                $('input#experiments').val(JSON.stringify(gaData['UA-25912405-1'].experiments));
            });
        }
    }

	/**
	 * Grabs the register server based on the country (legacy compatibility)
	 * @returns {string} Returns the register server based on the country
	 * @deprecated Use executePartnerRedirect() with dynamic partitionId routing instead
	 */
	function getRegisterServer() {
		let home_country = $('#countryCode').val();
		
		// Use the comprehensive EU country check instead of hardcoded list
		if (isEuServerCountry(home_country)) {
			return partnerLpAppSettings.registerServerEU; // "https://ss8.shipstation.com"
		}
		return partnerLpAppSettings.registerServer; // "https://ss15.shipstation.com"
	}

	/**
	 * 
	 * @returns {Function} Returns a function that generates a unique ID.
	 */
	function generateUniqueID() {
        function s4() {
            return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
        }
        return function() {
            return s4() + s4() + '-' + s4() + '-' + s4();
        };
    }

	/**
	 * Fires Segment identify and track calls for the Account Created event.
	 * Mirrors the tracking from register.js for use in partner registration flows.
	 *
	 * @param {Object} resp - Registration API response (must include userId, username, refno)
	 * @param {string} homeCountry - The user's home country code
	 */
	function trackSegmentAccountCreated(resp, homeCountry) {
		try {
			if (window.analytics && typeof window.analytics.track === 'function' && typeof window.analytics.identify === 'function') {
				analytics.identify(resp.userId, {
					"email": resp.username,
					"home_country": homeCountry
				});

				analytics.track("Account Created", {
					brand: 'ShipStation',
					utm_medium: $.cookie('utm_medium'),
					utm_source: $.cookie('utm_source'),
					utm_campaign: $.cookie('utm_campaign'),
					utm_term: $.cookie('utm_term'),
					utm_content: $.cookie('utm_content'),
					referral_code: $("#referral_code").val(),
					coupon: $('#coupon').length ? $('#coupon').val() : '',
					email: resp.username,
					home_country: homeCountry,
					affiliate: $("#affiliate").val(),
					referrer: $("#referrer").val() || "",
					phoneNumber: $("input[name=phone]").val(),
					company: $("#reg-compname").val(),
					howdidyouhearaboutus: $("#howdidyouhearaboutus").length ? $("#howdidyouhearaboutus").val() : "",
					fb__fbp: $.cookie('_fbp'),
					fb_fbc: $.cookie('_fbc'),
					fb_event_id: resp.userId,
					cje_cookie: $.cookie('cje'),
					customer_id: parseInt(resp.refno, 16),
					seller_id_hex: resp.refno,
					gclid_value: $.cookie('gclid'),
					shipment_volume: $("#shipment_volume").length ? $("#shipment_volume").val() : "",
					order_id: parseInt(resp.refno, 16),
					currency: "USD",
				});
			} else {
				console.warn('Account Created tracking skipped: Missing analytics');
			}
		} catch (e) {
			console.error('Error in Account Created tracking:', e);
		}
	}

	/**
	 * Captures the YOFI journey_id from the yfai_group_id cookie and populates
	 * the hidden journey_id form field. This is used for fraud detection purposes.
	 * 
	 * Captures the YOFI journey_id and populates the hidden form field.
	 * 
	 * @param {string} [fieldSelector='#journey_id'] - jQuery selector for the journey_id input field
	 */
	function captureJourneyId(fieldSelector = '#journey_id') {
		const journeyId = window.YofiTelemetry?.getJourneyId?.();
		if (journeyId) {
			$(fieldSelector).val(journeyId);
		}
	}

	// Return the method to make it accessible outside
	return {
		partnerLpAppSettings,
		getParameterByName,
		getShipStationUrl,
		getHomeCountry,
		ss_handleHowDidYouHearAboutUs,
		updateEuShipmentVolumeSelectOptions,
		updateTargetAppAtSubmission,
		registerCountryHandler,
		queryParamFromObject,
		checkIfExperimentsExist,
		waitForGlobalObject,
		getRegisterServer,
		generateUniqueID,
		// Country mapping utilities
		isEuServerCountry,
		// New auto-login redirect utilities
		getStep2RedirectPath,
		buildPartnerRedirectUrl,
		executePartnerRedirect,
		getPartnerBaseRedirectUrl,
		// YOFI fraud detection utilities
		captureJourneyId,
		// Segment tracking
		trackSegmentAccountCreated,
	};


})();

// window.partnersAppIntegrationModule = partnersAppIntegrationModule;

/**
 * Run partner form setup only after the Vue app in forms/signup.php has (re)built
 * #signup_form. That mount fully rebuilds the form DOM, discarding any jQuery handler /
 * plugin state (jQuery Validate) or plain .val() write made against the server-rendered
 * elements beforehand. Because the partner bundle is deferred, its $(document).ready fires
 * via a setTimeout that races Vue's DOMContentLoaded mount -- so binding in ready() worked
 * only "half the time" (when ready lost the race, everything bound to the doomed pre-mount
 * form). window.ssOnSignupFormReady (js/utils/cookieSetup.js) runs the callback once the
 * live form exists: immediately if already mounted, else when Vue signals readiness from
 * its mounted() hook. Falls back to $(document).ready if the helper isn't on the page.
 *
 * @param {Function} cb - Setup callback to run against the live, post-mount form.
 */
function onSignupFormReady(cb) {
	if (typeof window.ssOnSignupFormReady === 'function') {
		window.ssOnSignupFormReady(cb);
	} else {
		$(document).ready(cb);
	}
}

export { partnersAppIntegrationModule, onSignupFormReady };
